import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { ORCAMENTO_CILIA_HABILITADO } from '../api/orcamentoCiliaApi';
import { conectarCiliaSocket, desconectarCiliaSocket } from '../api/orcamentoCiliaSocket';
import useUserType from './useUserType';
import { papeisModuloNovo } from '../shared/modulos-novos';

/**
 * Papéis que enxergam o módulo — a união do `RoleGuard` das rotas no `App.tsx` com o
 * `@Roles` das rotas de leitura do backend: a lista de orçamentos é ORCAMENTO/ADMIN/TI e a
 * fila de autorizações é CHEFE_DE_OFICINA/ADMIN/TI.
 */
const PAPEIS_DO_MODULO = ['ORCAMENTO', 'CHEFE_DE_OFICINA', 'ADMIN', 'TI'];

/**
 * Conexão websocket do Orçamento Cilia (ADR 0003 do módulo) — montada UMA vez, no `App.tsx`.
 *
 * O que ela entrega: o badge do menu, o painel de avisos, a fila de autorização do Chefe e
 * as duas telas do módulo deixam de esperar o ciclo de 30 segundos. O polling **continua
 * existindo** como rede de segurança — socket é aceleração, nunca dependência: conexão
 * caída é a experiência anterior, não tela quebrada.
 *
 * ⚠️ **Gate triplo**, e os três importam:
 *  - **env do módulo**: sem ela a URL seria `undefined` e o módulo nem existe na UI;
 *  - **token**: sem sessão não há o que autenticar no handshake;
 *  - **papel**: sem isto, todo usuário logado abriria conexão com um backend que ele não
 *    usa — a lição do at-67 (BUG-V38), aqui com custo maior que uma requisição periódica.
 *
 * ⚠️ **O canal é `CustomEvent`, e aqui o motivo é ESTRUTURAL** (não só a lição do Portal):
 * este hook roda dentro do `App`, que é quem **renderiza** o `QueryClientProvider` — chamar
 * `useQueryClient` nesta posição estoura por falta de provider acima. Quem invalida query é
 * cada tela, que está dentro do provider; o Sidebar, que guarda estado próprio, refaz o
 * fetch do contador. Um canal só serve aos dois.
 */
export function useOrcamentoCiliaSocket() {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const userType = useUserType();

  useEffect(() => {
    if (!ORCAMENTO_CILIA_HABILITADO) return;
    if (!accessToken) return;
    // papeisModuloNovo (at-74): em teste, só o ADMIN abre a conexão. O Cilia saiu do teste em
    // 22/09/2026, então os papéis do módulo voltam a valer — a decisão vive em MODULOS_LIBERADOS.
    if (!userType || !papeisModuloNovo(PAPEIS_DO_MODULO, 'orcamento-cilia').includes(userType)) return;

    const socket = conectarCiliaSocket(accessToken);
    if (!socket) return;

    const aoReceberAviso = (detail: { tipo: string; orcamentoId: string | null }) => {
      window.dispatchEvent(new CustomEvent('cilia:notificacao', { detail }));
    };
    const aoAtualizarOrcamento = (detail: { orcamentoId: string; estado: string }) => {
      window.dispatchEvent(new CustomEvent('cilia:orcamento-atualizado', { detail }));
    };

    socket.on('notificacao:criada', aoReceberAviso);
    socket.on('orcamento:atualizado', aoAtualizarOrcamento);

    return () => {
      socket.off('notificacao:criada', aoReceberAviso);
      socket.off('orcamento:atualizado', aoAtualizarOrcamento);
      desconectarCiliaSocket();
    };
  }, [accessToken, userType]);
}
